import { useDeferredValue, useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { lineDiff, type RepositoryCommit, type RepositoryFile } from '@trove/core';
import { Columns, FileImage, FileJs, FileText, GitCommit, Rows } from '@phosphor-icons/react';
import { type ChangeStatus } from '../commitChanges';
import { folderChangedPaths, folderCommitHistory } from '../folderHistory';
import { FileContentPreview } from '../components/FileContentPreview';
import { LocDiffViewer } from '../components/LocDiffViewer';
import { RelativeTime } from '../components/RelativeTime';
import { isImageFile, isLineDiffable } from '../fileKinds';
import { diffStats, type DiffViewMode } from '../locDiff';
import { loadDiffViewMode, saveDiffViewMode, type DiffViewModePreference } from '../preferences';
import { useHydratedFiles } from '../useHydratedFiles';
import { useWorkspace } from '../workspace';
import shared from '../styles/shared.module.css';
import styles from './history.module.css';

interface HistorySearch {
  folder?: string;
}

export const Route = createFileRoute('/history')({
  validateSearch: (search: Record<string, unknown>): HistorySearch => ({
    folder: typeof search.folder === 'string' ? search.folder : undefined,
  }),
  component: HistoryPage,
});

/** Skip LCS when either side is huge — O(n·m) would freeze the tab. */
const MAX_DIFF_LINES = 4_000;
const MAX_DIFF_CELLS = 1_500_000;

function initialsFor(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0]![0] ?? ''}${parts[1]![0] ?? ''}`.toUpperCase();
  return name.slice(0, 2).toUpperCase() || '?';
}

function statusLabel(status: ChangeStatus) {
  if (status === 'added') return 'A';
  if (status === 'deleted') return 'D';
  return 'M';
}

function canLineDiff(before: string, after: string) {
  if (before === after) return true;
  const left = before.split('\n').length;
  const right = after.split('\n').length;
  if (left > MAX_DIFF_LINES || right > MAX_DIFF_LINES) return false;
  return left * right <= MAX_DIFF_CELLS;
}

function HistoryPage() {
  const { workspace, loading } = useWorkspace();
  const commits = workspace?.snapshot.commits ?? [];
  const { folder } = Route.useSearch();
  const visibleCommits = useMemo(
    () => folderCommitHistory(commits, folder ?? ''),
    [commits, folder],
  );
  const orderedCommits = useMemo(() => [...visibleCommits].reverse(), [visibleCommits]);
  const [selectedId, setSelectedId] = useState<string>();
  const [selectedPath, setSelectedPath] = useState<string>();
  const [diffMode, setDiffMode] = useState<DiffViewModePreference>(() => loadDiffViewMode());
  const selected =
    visibleCommits.find((commit) => commit.id === selectedId) ?? visibleCommits.at(-1);
  const parent = selected?.parentId
    ? commits.find((commit) => commit.id === selected.parentId)
    : undefined;
  const changes = useMemo(
    () => (selected ? folderChangedPaths(selected, parent, folder ?? '') : []),
    [selected, parent, folder],
  );
  const changedPath =
    (selectedPath && changes.some((change) => change.path === selectedPath)
      ? selectedPath
      : undefined) ?? changes[0]?.path;
  const beforeFile = parent?.files.find((file) => file.path === changedPath);
  const afterFile = selected?.files.find((file) => file.path === changedPath);
  const { files: hydratedPair, loading: hydratingDiff } = useHydratedFiles([beforeFile, afterFile]);
  const [hydratedBefore, hydratedAfter] = hydratedPair;
  const beforeContent = hydratedBefore?.content ?? '';
  const afterContent = hydratedAfter?.content ?? '';
  const deferredBefore = useDeferredValue(beforeContent);
  const deferredAfter = useDeferredValue(afterContent);
  const lineDiffable =
    (!hydratedBefore || isLineDiffable(hydratedBefore)) &&
    (!hydratedAfter || isLineDiffable(hydratedAfter));
  const imagePreview = isImageFile(hydratedBefore) || isImageFile(hydratedAfter);
  const diffReady =
    !hydratingDiff && deferredBefore === beforeContent && deferredAfter === afterContent;
  const diffTooLarge =
    lineDiffable && changedPath ? !canLineDiff(deferredBefore, deferredAfter) : false;
  const diff = useMemo(() => {
    if (!lineDiffable || !changedPath || diffTooLarge || hydratingDiff) return [];
    if (deferredBefore === deferredAfter) return [];
    return lineDiff(deferredBefore, deferredAfter);
  }, [lineDiffable, changedPath, diffTooLarge, hydratingDiff, deferredBefore, deferredAfter]);
  const diffCounts = useMemo(() => diffStats(diff), [diff]);
  const showLineDiff = Boolean(changedPath) && lineDiffable && !diffTooLarge && diffReady;
  const changeDiffMode = (mode: DiffViewMode) => {
    setDiffMode(mode);
    saveDiffViewMode(mode);
  };

  if (loading) {
    return (
      <div className={shared.splash}>
        <div className={shared.splashInner}>
          <div className={shared.spinner} />
          Loading history…
        </div>
      </div>
    );
  }

  if (!workspace) return <div className={shared.empty}>No repository history is available.</div>;
  return (
    <div className={styles.page} data-full-bleed>
      <div className={styles.timelineLayout}>
        <div className={styles.commitList}>
          {orderedCommits.map((commit) => (
            <CommitRow
              key={commit.id}
              commit={commit}
              selected={commit.id === selected?.id}
              onSelect={() => {
                setSelectedId(commit.id);
                setSelectedPath(undefined);
              }}
            />
          ))}
        </div>
        <div className={styles.fileList} aria-label="Changed files">
          {changes.length === 0 ? (
            <div className={styles.fileListEmpty}>No file changes</div>
          ) : (
            changes.map((change) => {
              const name = change.path.split('/').at(-1) ?? change.path;
              return (
                <button
                  key={change.path}
                  type="button"
                  className={`${styles.fileRow} ${change.path === changedPath ? styles.fileRowSelected : ''}`}
                  onClick={() => setSelectedPath(change.path)}
                  title={change.path}
                >
                  <span
                    className={`${styles.fileStatus} ${styles[`status_${change.status}`]}`}
                    aria-label={change.status}
                  >
                    {statusLabel(change.status)}
                  </span>
                  <span className={styles.fileName}>{name}</span>
                  <span className={styles.filePath}>{change.path}</span>
                </button>
              );
            })
          )}
        </div>
        <div className={styles.diff}>
          <div className={styles.diffHeader}>
            {changedPath ? (
              <>
                {imagePreview ? (
                  <FileImage size={12} />
                ) : lineDiffable ? (
                  <FileJs size={12} />
                ) : (
                  <FileText size={12} />
                )}
                <span className={styles.diffHeaderPath}>
                  {changedPath} · {selected?.id.slice(0, 10)}
                </span>
              </>
            ) : (
              <span className={styles.diffHeaderPath}>
                {changes.length === 0 ? 'No file changes' : 'Select a file'} ·{' '}
                {selected?.id.slice(0, 10)}
              </span>
            )}
            {showLineDiff && (
              <>
                <span className={styles.diffStats} aria-label="Diff statistics">
                  <span className={styles.additions}>+{diffCounts.additions}</span>
                  <span className={styles.deletions}>−{diffCounts.deletions}</span>
                </span>
                <span className={styles.modeToggle} role="group" aria-label="Diff layout">
                  <button
                    type="button"
                    className={`${styles.modeButton} ${diffMode === 'unified' ? styles.modeButtonActive : ''}`}
                    aria-pressed={diffMode === 'unified'}
                    onClick={() => changeDiffMode('unified')}
                  >
                    <Rows size={11} />
                    Stacked
                  </button>
                  <button
                    type="button"
                    className={`${styles.modeButton} ${diffMode === 'split' ? styles.modeButtonActive : ''}`}
                    aria-pressed={diffMode === 'split'}
                    onClick={() => changeDiffMode('split')}
                  >
                    <Columns size={11} />
                    Side by side
                  </button>
                </span>
              </>
            )}
          </div>
          {!changedPath ? (
            <div className={styles.diffEmpty}>
              {changes.length === 0
                ? 'This commit only updated repository metadata.'
                : 'Select a file to view its diff.'}
            </div>
          ) : lineDiffable ? (
            diffTooLarge ? (
              <div className={styles.diffEmpty}>
                Diff is too large to render inline. Download the file versions from Files to compare
                locally.
              </div>
            ) : !diffReady ? (
              <div className={styles.diffEmpty}>
                {hydratingDiff ? 'Loading file versions…' : 'Computing diff…'}
              </div>
            ) : (
              <LocDiffViewer lines={diff} mode={diffMode} />
            )
          ) : (
            <PreviewDiffPane before={hydratedBefore} after={hydratedAfter} />
          )}
        </div>
      </div>
    </div>
  );
}

function AuthorAvatar({ name, src }: { name: string; src?: string }) {
  return (
    <span className={styles.avatar} aria-hidden>
      {src ? <img src={src} alt="" /> : initialsFor(name)}
    </span>
  );
}

function CommitRow({
  commit,
  selected,
  onSelect,
}: {
  commit: RepositoryCommit;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      className={`${styles.commit} ${selected ? styles.commitSelected : ''}`}
      onClick={onSelect}
    >
      <span className={styles.marker} aria-hidden>
        <GitCommit size={12} />
      </span>
      <span className={styles.commitBody}>
        <span className={styles.commitTitle}>
          <AuthorAvatar name={commit.author} />
          <span className={styles.commitMessage}>{commit.message}</span>
        </span>
        <span className={styles.commitMeta}>
          <span>
            {commit.author} · <RelativeTime value={commit.createdAt} />
          </span>
          <span className={styles.commitHash}>{commit.id.slice(0, 7)}</span>
        </span>
      </span>
    </button>
  );
}

function PreviewDiffPane({ before, after }: { before?: RepositoryFile; after?: RepositoryFile }) {
  const unchanged = Boolean(
    before &&
    after &&
    (before.contentHash
      ? before.contentHash === after.contentHash
      : before.content === after.content),
  );

  if ((before && before.content === undefined) || (after && after.content === undefined)) {
    return <div className={styles.diffEmpty}>Loading file versions…</div>;
  }

  return (
    <div className={styles.imageDiff}>
      {before ? (
        <figure className={`${styles.imagePanel} ${styles.imagePanelRemove}`}>
          <figcaption>{unchanged ? 'Previous' : after ? 'Before' : 'Deleted'}</figcaption>
          <FileContentPreview file={before} className={styles.previewPanel} />
        </figure>
      ) : null}
      {after ? (
        <figure className={`${styles.imagePanel} ${styles.imagePanelAdd}`}>
          <figcaption>{unchanged ? 'Current' : before ? 'After' : 'Added'}</figcaption>
          <FileContentPreview file={after} className={styles.previewPanel} />
        </figure>
      ) : null}
      {!before && !after ? (
        <div className={styles.imageEmpty}>No preview available for this change.</div>
      ) : null}
    </div>
  );
}
